import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Sun,
  Moon,
  LogOut,
  LayoutDashboard,
  Plus,
  Users,
  BarChart3,
  Menu,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Logo } from "./Logo";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

const navItems = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/new", label: "New", icon: Plus, testid: "nav-new-timer" },
  { to: "/teams", label: "Teams", icon: Users, testid: "nav-teams" },
  { to: "/analytics", label: "Stats", icon: BarChart3, testid: "nav-analytics" },
];

export const Navbar = () => {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const onLogout = () => {
    setMobileOpen(false);
    logout();
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-30 border-b border-border bg-background/90 backdrop-blur">
      <div className="mx-auto flex w-full max-w-7xl items-center justify-between gap-2 px-4 py-3 sm:px-6 sm:py-4">
        {/* Logo always visible; min-w-0 prevents flex children from forcing overflow */}
        <div className="min-w-0 flex-shrink-0">
          <Logo />
        </div>

        {/* Desktop nav — hidden below md */}
        <div className="hidden md:flex items-center gap-2">
          {user ? (
            <>
              {navItems.map(({ to, label, icon: Icon, testid }) => (
                <Link key={to} to={to}>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="rounded-none font-mono text-xs uppercase tracking-widest"
                    data-testid={testid}
                  >
                    <Icon className="mr-2 h-4 w-4" /> {label}
                  </Button>
                </Link>
              ))}
              <div className="hidden lg:block font-mono text-xs text-muted-foreground px-3 truncate max-w-[180px]">
                <span className="text-primary">{">"}</span> {user.email}
              </div>
            </>
          ) : (
            <>
              <Link to="/login">
                <Button
                  variant="ghost"
                  size="sm"
                  className="rounded-none font-mono text-xs uppercase tracking-widest"
                  data-testid="nav-login"
                >
                  Login
                </Button>
              </Link>
              <Link to="/register">
                <Button
                  size="sm"
                  className="rounded-none font-mono text-xs uppercase tracking-widest"
                  data-testid="nav-register"
                >
                  Initialize
                </Button>
              </Link>
            </>
          )}
          <Button
            variant="outline"
            size="icon"
            className="rounded-none border-border"
            onClick={toggle}
            data-testid="theme-toggle"
            aria-label="Toggle theme"
          >
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
          {user && (
            <Button
              variant="outline"
              size="icon"
              className="rounded-none border-border"
              onClick={onLogout}
              data-testid="nav-logout"
              aria-label="Logout"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* Mobile controls — visible below md only */}
        <div className="flex md:hidden items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            className="rounded-none border-border h-9 w-9"
            onClick={toggle}
            data-testid="theme-toggle-mobile"
            aria-label="Toggle theme"
          >
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="rounded-none border-border h-9 w-9"
                aria-label="Open menu"
                data-testid="nav-mobile-trigger"
              >
                <Menu className="h-4 w-4" />
              </Button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="w-[82vw] max-w-xs rounded-none border-l border-border p-0"
              data-testid="nav-mobile-sheet"
            >
              <SheetHeader className="border-b border-border px-5 py-4 text-left">
                <SheetTitle className="font-mono text-xs uppercase tracking-[0.3em] text-muted-foreground">
                  <span className="text-primary">{">"}</span> Menu
                </SheetTitle>
                <SheetDescription className="sr-only">
                  Primary navigation for the GTGS Pomodoro app
                </SheetDescription>
              </SheetHeader>
              <nav className="flex flex-col px-3 py-4">
                {user ? (
                  <>
                    {navItems.map(({ to, label, icon: Icon, testid }) => (
                      <Link
                        key={to}
                        to={to}
                        onClick={() => setMobileOpen(false)}
                        data-testid={`${testid}-mobile`}
                        className="flex items-center gap-3 px-3 py-3 font-mono text-sm uppercase tracking-widest text-foreground hover:bg-secondary"
                      >
                        <Icon className="h-4 w-4 text-primary" />
                        {label}
                      </Link>
                    ))}
                    <div className="mt-4 border-t border-border px-3 py-3 font-mono text-xs text-muted-foreground truncate">
                      <span className="text-primary">{">"}</span> {user.email}
                    </div>
                    <button
                      type="button"
                      onClick={onLogout}
                      data-testid="nav-logout-mobile"
                      className="mt-2 flex items-center gap-3 px-3 py-3 font-mono text-sm uppercase tracking-widest text-destructive hover:bg-secondary"
                    >
                      <LogOut className="h-4 w-4" />
                      Logout
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      to="/login"
                      onClick={() => setMobileOpen(false)}
                      data-testid="nav-login-mobile"
                      className="flex items-center gap-3 px-3 py-3 font-mono text-sm uppercase tracking-widest text-foreground hover:bg-secondary"
                    >
                      Login
                    </Link>
                    <Link
                      to="/register"
                      onClick={() => setMobileOpen(false)}
                      data-testid="nav-register-mobile"
                      className="mt-2 flex items-center gap-3 bg-primary px-3 py-3 font-mono text-sm uppercase tracking-widest text-primary-foreground hover:bg-primary/90"
                    >
                      Initialize
                    </Link>
                  </>
                )}
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
